import { APIRequestStatus } from 'core/constants/redux';
import { IPanelDataResponse } from 'core/models/dashboard';
import { IDimension } from 'core/models/report-redux';
import {
  IBuilder,
  IPaging,
  IReportConfig, ISorting, IRawTemplate, ITemplateSetting, JoinItem, ReportBuilderStatus, SortingOrderTypes,
  ICustomHierarchy,
} from 'core/models/report-builder/report-builder';
import { FilterModel, ObjModel, ReportResponseModel } from 'core/models';
import {
  IField, IColumn, IFilterConfig, IColumnFormatter,
  ICustomFilterDefinition,
  IDimensionMetaField,
} from 'core/models/report-response';
import {
  ColumnDataTypes, ColumnModes, DataSourceSelectionType, DimensionMode, FieldEntitiesType, draggableItemType,
} from 'core/constants/report';
import { FilterType, UserFilterPayloadType } from 'core/constants/filter';
import { IExpression } from 'core/models/report-expressions';
import { IReportDetails } from 'redux-v2/global-store/global-store.state';
import { IFilterResponse } from 'core/models/filter';
import { IVariables } from 'core/models/store';
import { IBreadcrumbItem } from 'components/common/Breadcrumbs/breadcrumbs.component';
import { SubQueryPanelType } from 'core/utils/report-builder.util';
import { PageContext } from 'core/constants/common';
import { FORMAT_TYPES } from 'components/feature/Report/ReportSidebar/ChartsVisualization/ConditionalFormatting/constants';

export interface IReportBuilderState {
  requestProcessing: IRequestProcessing;
  reportBuilderInfo: IReportBuilderInfo;
  updatedReportBuilderInfo: IUpdatedReportBuilderInfo;
  dataSourceInfo: IDataSourceInfo;
  assortedInfo: IAssortedInfo;
  previewData: IPreviewData;
  filterBuilder: IFilterBuilder;
  previewBackUpInfo: IPreviewBackUpInfo;
  reportEditInfo: IReportEditInfo;
  reportQueryBuilder: IQueryBuilder;
}

export interface ICustomExpression{
  displayName: string;
  description?: string;
  jsonExpression?: IExpression;
  uniqueId?: string | number;
  Mode: ColumnModes;
  Formatter:IColumnFormatter;
  DataType:ColumnDataTypes;
  dimensionType?: DimensionMode;
  ExpressionType: ExpressionContext;
}

export interface SaveSubQueryPayload {
  queryName: string;
  partitionColumnFilterDateLimit: number;
}

export enum ExpressionContext {
  Measure = 'Measure',
  Dimension = 'Dimension',
  Custom = 'Custom'
}

export interface IPreviewData {
  data: IPanelDataResponse;
  mappedMetaFields: object;
  error?: IPreviewDataError; // API error message from preview request
}

export interface IPreviewDataError {
  ErrorMessage:IErrorMessage ;
}

export interface IErrorMessage{
  Header: string;
  Message: string;
}
export interface IQueryPreviewData{
    data: IPanelDataResponse;
    mappedMetaFields: object;
    previewQueryId: string;
    previewBackupInfo: {
      previewInfo: IQueryBuilderUpdatedInfo
    }
    error?: IPreviewDataError;
}

// to hold the saved preview data for comparision
export interface IPreviewBackUpInfo {
  updatedReportBuilderInfo: IUpdatedReportBuilderInfo;
  filterBuilder: IFilterBuilder;
}

export enum ReportBuilderSaveStatus {
  DRAFT = 1,
  CREATED,
  EDITED,
}

export interface IUpdateRequestProcessingPayload {
  type: ReportBuilderActions;
  status: APIRequestStatus;
}

export interface IRequestProcessing {
  [ReportBuilderActions.INITIAL_SAVE_TEMPLATE]: APIRequestStatus;
  [ReportBuilderActions.SAVE_REPORT_CONFIG]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_FILTERS]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_DIMENSIONS]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_MEASURES]: APIRequestStatus;
  [ReportBuilderActions.GET_PREVIEW_DATA]: APIRequestStatus;
  [ReportBuilderActions.FILTER_DATA_LOAD_REQUEST]: ObjModel.ObjGeneric<APIRequestStatus>;
  [ReportBuilderActions.SUB_QUERY_FILTER_LOAD_DATA_REQUEST]: ObjModel.ObjGeneric<APIRequestStatus>;
  [ReportBuilderActions.GET_FIELDS]: APIRequestStatus;
  [ReportBuilderActions.GET_REPORT_RAW_CONFIG_REQUEST]: APIRequestStatus;
  [ReportBuilderActions.GET_REPORT_RENDER_CONFIG_REQUEST]: APIRequestStatus;
  [ReportBuilderActions.GET_MEASURE_DRILLDOWN_CONFIG]: APIRequestStatus;
  [ReportBuilderActions.GET_DRILLDOWN_CONFIG]: APIRequestStatus;
  [ReportBuilderActions.SAVE_DRILLDOWN_CONFIG]: APIRequestStatus;
  [ReportBuilderActions.GET_DEFAULT_DRILLDOWN_COLUMN]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_DRILLDOWN_COLUMN]: APIRequestStatus;
  [ReportBuilderActions.GET_RAW_TEMPLATE_DETAILS]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_USER_FIELD_FILTERS]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_USER_DIMENSIONS]: APIRequestStatus
  [ReportBuilderActions.GET_DATA_SOURCES_REQUEST]:APIRequestStatus;
   [ReportBuilderActions.SAVE_DATA_SOURCE_CHANGES_REQUEST_RESET]:APIRequestStatus;
  [ReportBuilderActions.SAVE_DATA_SOURCE_CHANGES_REQUEST]:APIRequestStatus
  [ReportBuilderActions.GET_ADDITIONAL_DIMENSIONS_FOR_NAMESPACES]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_FILTERS_FOR_NAMESPACES]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_MEASURES_FOR_NAMESPACES]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_USER_FIELDS_FOR_NAMESPACES]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_USER_FILTERS_FOR_NAMESPACES]: APIRequestStatus;
  [ReportBuilderActions.GET_ADDITIONAL_FIELDS_FOR_NAMESPACES]: APIRequestStatus;
  [ReportBuilderActions.SAVE_NESTED_JOINS_CONFIG]: APIRequestStatus;
  [ReportBuilderActions.GET_SUB_QUERY_PREVIEW_DATA]: APIRequestStatus;
  [ReportBuilderActions.GET_META_SELECTOR_OPTIONS]: APIRequestStatus;
  [ReportBuilderActions.GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_BUILDER]: APIRequestStatus;
  [ReportBuilderActions.GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_SUBBUILDER]: APIRequestStatus;
  [ReportBuilderActions.REPORT_BUILDER_TYPE_CHANGE_REQUEST]: APIRequestStatus;
  [ReportBuilderActions.GET_DATA_SOURCES_FIELDS_REQUEST]: APIRequestStatus;
  [ReportBuilderActions.GET_ALL_REPORTS_FOR_LINKED_INSIGHTS]: APIRequestStatus;
  [ReportBuilderActions.GET_REPORT_CONFIG_FOR_LINKED_INSIGHTS]: ObjModel.ObjGeneric<APIRequestStatus>;
}

export interface ILinkedInsightReport {
  label: string;
  value: string;
}

export interface ILinkedInsightReportConfig {
  Dimensions: ReportResponseModel.IColumn[];
  Filters: ReportResponseModel.IFilterConfig[];
  Details: ReportResponseModel.IConfigDetails;
  CustomFilterDefinitions?: ReportResponseModel.ICustomFilterDefinition[];
}

export interface IReportBuilderInfo {
  additionalFilters: Array<ReportResponseModel.IFilterConfig>;
  additionalDimensions: Array<ReportResponseModel.IColumn>;
  additionalMeasures: Array<ReportResponseModel.IColumn>;
  additionalUserFieldFilters: Array<ReportResponseModel.IFilterConfig>;
  additionalUserFields: Array<ReportResponseModel.IColumn>;
  fields:Array<IField>;
  allReports: ILinkedInsightReport[];
  reportConfigMapping: ObjModel.ObjGeneric<ILinkedInsightReportConfig>;
}

export interface IUpdatedReportBuilderInfo {
  selectedTemplate: IRawTemplate;
  reportBuilderSavePayload: IReportConfig;
  appliedDimensions: IDimension[];
  appliedMeasures: IColumn[];
  sortState: ISorting[];
  calculatedColumns: Set<string>;
  expressionStore: ObjModel.ObjGeneric<ICustomExpression>;
  drilldownConfigInfo: IdrilldownConfigInfo;
  entityGroupOptionsMap: Record<string, IDimensionMetaField[]>;
  entityGroupStore: Record<string, string>;
  customHierarchies: ICustomHierarchy[];
  conditionalFormatting: IConditionalFormatting;
  isCurrentlyEditingOrAddingFormattingRule: boolean;
  linkedReportsConfig: ILinkedConfig[];
}

export interface IdrilldownConfigInfo{
  drilldownConfigMapping: DrilldownConfigMapping,
  drilldownColumnConfig: DrilldownColumnConfig,
  enableDrilldown: boolean,
  drilldownPagination: IPaging,
}
export interface IAssortedInfo {
  isFormDisabled: boolean;
  selectedTabInfo: string;
  reportId: string;
  settings: ITemplateSetting;
  reportBuilderSaveStatus: ReportBuilderSaveStatus;
  isNestedQueryFormOpen: boolean;
  isDataSourceEditMode: boolean;
  activeSubQueryPanel: SubQueryPanelType;
  isDataSourceSidetrayExpanded: boolean;
  isDrilldownMeasuresPanelExpanded: boolean,
  isDrilldownFieldsPanelExpanded: boolean,
  isReportDataSourceSaved: boolean
}

export interface IFilterBuilder {
  filterConfig: Array<IFilterConfig>;
  appliedFilters: ObjModel.ObjGeneric<FilterModel.IFilterResponse>;
  isUsersModified: boolean;
  filterMetaInfo: IFilterMetaInfo[];
  UserDropdownSelectType?: UserFilterPayloadType;
  customFilterDefinitions: ICustomFilterDefinition[];
}

export interface IFilterMetaInfo {
  IsPartitionColumn: boolean;
  Entity: FieldEntitiesType;
  NamespaceEntity: string;
  NsDisplayName: string;
  Namespace: string;
  ID: string;
  Metadata?: ObjModel.Obj;
  Alias: string;
  Label: string;
  LinkedDimensionAlias: string;
  LinkedTo: string;
  Operator: string;
  Type?: FilterType;
}

export interface IFilterMetaInfoStore {
  Entity: FieldEntitiesType;
  NamespaceEntity: string;
  NsDisplayName: string;
  Namespace: string;
  IsPartitionColumn: boolean;
  Metadata?: ObjModel.Obj;
  LinkedTo: string;
  Label?: string;
}

export interface IEditProfilePayload {
  Profiles:Array<string>;
  Reports: Array<IReportDetails>;
}

export interface IReportEditInfo {
  rawReportConfig: IReportConfig;
  renderReportConfig: ReportResponseModel.IReportConfig;
  combineConfigLoaded: boolean;
  isEditing: boolean;
  temporaryReportId: string;
}

export enum ReportBuilderActions {
  GET_TEMPLATE_LIST = '[RB] GET_TEMPLATE_LIST',
  UPDATED_SELECTED_TEMPLATE = '[RB] UPDATED_SELECTED_TEMPLATE',
  INITIAL_SAVE_TEMPLATE = '[RB] INITIAL_SAVE_TEMPLATE',
  GET_ADDITIONAL_FILTERS = '[RB] GET_ADDITIONAL_FILTERS',
  GET_ADDITIONAL_USER_FIELD_FILTERS = '[RB] GET_ADDITIONAL_USER_FIELD_FILTERS',
  GET_ADDITIONAL_MEASURES = '[RB] GET_ADDITIONAL_MEASURES',
  GET_ADDITIONAL_DIMENSIONS = '[RB] GET_ADDITIONAL_DIMENSIONS',
  FILTER_DATA_LOAD_REQUEST = '[RB] FILTER_DATA_LOAD_REQUEST',
  UPDATE_REPORT_TEMPLATE_DETAIL = '[RB] UPDATE_REPORT_TEMPLATE_DETAIL',
  SEARCH_FILTER_DATA_LOAD_REQUEST = '[RB] SEARCH_FILTER_DATA_LOAD_REQUEST',
  LOAD_MORE_FILTER_DATA_LOAD_REQUEST = '[RB] LOAD_MORE_FILTER_DATA_LOAD_REQUEST',
  ADD_DYNAMIC_FILTER = '[RB] ADD_DYNAMIC_FILTER',
  ADD_CUSTOM_FILTER = '[RB] ADD_CUSTOM_FILTER',
  UPDATE_CUSTOM_FILTER = '[RB] UPDATE_CUSTOM_FILTER',
  DELETE_CUSTOM_FILTER = '[RB] DELETE_CUSTOM_FILTER',
  SWITCH_USER_FIELD_AND_FILTER = '[RB] SWITCH_USER_FIELD_AND_FILTER',
  DELETE_DYNAMIC_FILTER = '[RB] DELETE_DYNAMIC_FILTER',
  FILTER_VALUE_CHANGE = '[RB] FILTER_VALUE_CHANGE',
  RESET_REPORT_TEMPLATE = '[RB] RESET_REPORT_TEMPLATE',
  SAVE_REPORT_CONFIG = '[RB] SAVE_REPORT_CONFIG',
  UPDATE_SELECTED_TAB = '[RB] UPDATE_SELECTED_TAB',
  UPDATE_VISUALIZATION = '[RB] UPDATE_VISUALIZATION',
  INITIALIZE_COMBO_CONFIG = '[RB] INITIALIZE_COMBO_CONFIG',
  UPDATE_COMBO_MEASURE_CONFIG = '[RB] UPDATE_COMBO_MEASURE_CONFIG',
  ADD_COMBO_YAXIS_COLUMN = '[RB] ADD_COMBO_YAXIS_COLUMN',
  REMOVE_COMBO_YAXIS_COLUMN = '[RB] REMOVE_COMBO_YAXIS_COLUMN',
  ADD_COMBO_XAXIS_COLUMN = '[RB] ADD_COMBO_XAXIS_COLUMN',
  REMOVE_COMBO_XAXIS_COLUMN = '[RB] REMOVE_COMBO_XAXIS_COLUMN',
  CLEAR_COMBO_CONFIG = '[RB] CLEAR_COMBO_CONFIG',
  GET_PREVIEW_DATA = '[RB] GET_PREVIEW_DATA',
  SET_DIMENSIONS_GROUPING = '[RB] SET_DIMENSIONS_GROUPING',
  SET_DIMENSIONS_BINNING = '[RB] SET_DIMENSIONS_BINNING',
  UPDATE_PAGINATION = '[RB] UPDATE_PAGINATION',
  ADD_MEASURE = '[RB] ADD_MEASURE',
  REM_MEASURE = '[RB] REM_MEASURE',
  MOV_MEASURE = '[RB] MOV_MEASURE',
  SET_MEASURE = '[RB] SET_MEASURE',
  UPDATE_FILTER_FORM = '[RB] UPDATE_FILTER_FORM',
  UPDATE_SAVE_REPORT_BUILDER_STATUS = '[RB] UPDATE_SAVE_REPORT_BUILDER_STATUS',
  SET_REQUEST_PROCESSING_STATE = '[RB] SET_REQUEST_PROCESSING_STATE',
  SORT_COLUMN = '[RB] SORT_COLUMN',
  ADD_OR_UPDATE_CUSTOM_EXPRESSION = '[RB] ADD_OR_UPDATE_CUSTOM_EXPRESSION',
  DELETE_CUSTOM_EXPRESSION = '[RB] DELETE_CUSTOM_EXPRESSION',
  GET_FIELDS = '[RB] GET_FIELDS',
  GET_FIELDS_FAILURE = '[RB] GET_FIELDS_FAILURE',
  GET_FIELDS_SUCCESS = '[RB] GET_FIELDS_SUCCESS',
  GET_REPORT_RAW_CONFIG_REQUEST = '[RB] GET_REPORT_RAW_CONFIG_REQUEST',
  GET_REPORT_RENDER_CONFIG_REQUEST = '[RB] GET_REPORT_RENDER_CONFIG_REQUEST',
  COMBINE_RAW_AND_RENDER_CONFIG = '[RB] COMBINE_RAW_AND_RENDER_CONFIG',
  SET_REPORT_EDIT_ID = '[RB] SET_REPORT_EDIT_ID',
  SET_TEMPORARY_REPORT_ID = '[RB] SET_TEMPORARY_REPORT_ID',
  SET_INITIAL_SUCCESS_STATE = '[RB] SET_INITIAL_SUCCESS_STATE',
  SET_INITIAL_FAILURE_STATE = '[RB] SET_INITIAL_FAILURE_STATE',
  SET_PREVIEW_DATA_SUCCESS_STATE = '[RB] SET_PREVIEW_DATA_SUCCESS_STATE',
  SET_PREVIEW_DATA_FAILURE_STATE = '[RB] SET_PREVIEW_DATA_FAILURE_STATE',
  GET_MEASURE_DRILLDOWN_CONFIG = '[RB] GET_MEASURE_DRILLDOWN_CONFIG',
  ADD_DRILLDOWN_CONFIGURATION = '[RB] ADD_DRILLDOWN_CONFIGURATION',
  ADD_DRILLDOWN_MEASURE = '[RB] ADD_DRILLDOWN_MEASURE',
  SET_SELECTED_MEASURE = '[RB] SET_SELECTED_MEASURE',
  REMOVE_DRILLDOWN_ACCORD = '[RB] REMOVE_DRILLDOWN_ACCORD',
  REMOVE_DRILLDOWN_MEASURE = '[RB] REMOVE_DRILLDOWN_MEASURE',
  INIT_DRILLDOWN_MAPPING = '[RB] INIT_DRILLDOWN_MAPPING',
  SORT_DRILLDOWN_COLUMN = '[RB] SORT_DRILLDOWN_COLUMN',
  ADD_REMOVE_DRILLDOWN_FIELD = '[RB] ADD_REMOVE_DRILLDOWN_FIELD',
  UPDATE_DRILLDOWN_COLUMN = '[RB] UPDATE_DRILLDOWN_COLUMN',
  MOVE_DRILLDOWN_COLUMN = '[RB] MOVE_DRILLDOWN_COLUMN',
  TOGGLE_DRILLDOWN = '[RB] TOGGLE_DRILLDOWN',
  UPDATE_DRILLDOWN_PAGINATION = '[RB] UPDATE_DRILLDOWN_PAGINATION',
  GET_DRILLDOWN_CONFIG = '[RB] GET_DRILLDOWN_CONFIG',
  SAVE_DRILLDOWN_CONFIG = '[RB] SAVE_DRILLDOWN_CONFIG',
  GET_DEFAULT_DRILLDOWN_COLUMN = '[RB] GET_DEFAULT_DRILLDOWN_COLUMN',
  GET_ADDITIONAL_DRILLDOWN_COLUMN = '[RB] GET_ADDITIONAL_DRILLDOWN_COLUMN',
  GET_RAW_TEMPLATE_DETAILS = '[RB] GET_RAW_TEMPLATE_DETAILS',
  GET_RAW_TEMPLATE_DETAILS_SUCCESS = '[RB] GET_RAW_TEMPLATE_DETAILS_SUCCESS',
  GET_RAW_TEMPLATE_DETAILS_FAILURE = '[RB] GET_RAW_TEMPLATE_DETAILS_FAILURE',
  RESET_GET_RAW_TEMPLATE_DETAILS = '[RB] RESET_GET_RAW_TEMPLATE_DETAILS',
  SAVE_DRILLDOWNCOLS_WITH_REPORT_CONFIG = 'SAVE_DRILLDOWNCOLS_WITH_REPORT_CONFIG',
  GET_ADDITIONAL_USER_DIMENSIONS = '[RB] GET_ADDITIONAL_USER_DIMENSIONS',
  GET_DATA_SOURCES_REQUEST = '[RB] GET_DATA_SOURCES_REQUEST',
  UPDATE_DATA_SOURCE_CHANGES = '[RB] UPDATE_DATA_SOURCE_CHANGES',
  SAVE_DATA_SOURCE_CHANGES_REQUEST_RESET = '[RB] SAVE_DATA_SOURCE_CHANGES_REQUEST_RESET',
  SAVE_DATA_SOURCE_CHANGES_REQUEST = '[RB] SAVE_DATA_SOURCE_CHANGES_REQUEST',
  SAVE_DATA_SOURCE_CHANGES_SUCCESS = '[RB] SAVE_DATA_SOURCE_CHANGES_SUCCESS',
  SAVE_DATA_SOURCE_CHANGES_FAILURE = '[RB] SAVE_DATA_SOURCE_CHANGES_FAILURE',
  TOGGLE_DATA_SOURCE_EDIT_MODE = '[RB] TOGGLE_DATA_SOURCE_EDIT_MODE',
  RESET_REPORT_CONFIG_FOR_DATA_SOURCE = '[RB] RESET_REPORT_CONFIG_FOR_DATA_SOURCE ',
  UPDATE_REPORT_METADATA_POST_JOINS_SAVE = '[RB] UPDATE_REPORT_METADATA_POST_JOINS_SAVE',
  UPDATE_DATA_SOURCE_SIDETRAY_STATE = '[RB] UPDATE_DATA_SOURCE_SIDETRAY_STATE',
  GET_ADDITIONAL_DIMENSIONS_FOR_NAMESPACES = '[RB] GET_ADDITIONAL_DIMENSIONS_FOR_NAMESPACES',
  GET_ADDITIONAL_FILTERS_FOR_NAMESPACES = '[RB] GET_ADDITIONAL_FILTERS_FOR_NAMESPACES',
  GET_ADDITIONAL_MEASURES_FOR_NAMESPACES = '[RB] GET_ADDITIONAL_MEASURES_FOR_NAMESPACES',
  GET_ADDITIONAL_FIELDS_FOR_NAMESPACES = '[RB] GET_ADDITIONAL_FIELDS_FOR_NAMESPACES',
  GET_ADDITIONAL_USER_FIELDS_FOR_NAMESPACES = '[RB] GET_ADDITIONAL_USER_FIELDS_FOR_NAMESPACES',
  GET_ADDITIONAL_USER_FILTERS_FOR_NAMESPACES = '[RB] GET_ADDITIONAL_USER_FILTERS_FOR_NAMESPACES',
  SET_QUERY_BUILDER_DIMENSIONS_GROUPING = '[RB] SET_QUERY_BUILDER_DIMENSIONS_GROUPING',
  SET_QUERY_BUILDER_DIMENSIONS_BINNING = '[RB] SET_QUERY_BUILDER_DIMENSIONS_BINNING',
  ADD_QUERY_BUILDER_MEASURE = '[RB] ADD_QUERY_BUILDER_MEASURE',
  REM_QUERY_BUILDER_MEASURE = '[RB] REM_QUERY_BUILDER_MEASURE',
  MOV_QUERY_BUILDER_MEASURE = '[RB] MOV_QUERY_BUILDER_MEASURE',
  SET_QUERY_BUILDER_MEASURE = '[RB] SET_QUERY_BUILDER_MEASURE',
  UPDATE_QUERY_BUILDER_FILTER_FORM = '[RB] UPDATE_QUERY_BUILDER_FILTER_FORM',
  ADD_OR_UPDATE_QUERY_BUILDER_CUSTOM_EXPRESSION = '[RB] ADD_OR_UPDATE_QUERY_BUILDER_CUSTOM_EXPRESSION',
  DELETE_QUERY_BUILDER_CUSTOM_EXPRESSION = '[RB] DELETE_QUERY_BUILDER_CUSTOM_EXPRESSION',
  ADD_QUERY_BUILDER_DYNAMIC_FILTER = '[RB] ADD_QUERY_BUILDER_DYNAMIC_FILTER',
  SWITCH_QUERY_BUILDER_USER_FIELD_AND_FILTER = '[RB] SWITCH_QUERY_BUILDER_USER_FIELD_AND_FILTER',
  DELETE_QUERY_BUILDER_DYNAMIC_FILTER = '[RB] DELETE_QUERY_BUILDER_DYNAMIC_FILTER',
  QUERY_BUILDER_FILTER_VALUE_CHANGE = '[RB] QUERY_BUILDER_FILTER_VALUE_CHANGE',
  CREATE_NEW_QUERY = '[RB] CREATE_NEW_QUERY',
  TOGGLE_NESTED_QUERY_BUILDER_MODE = '[RB] TOGGLE_NESTED_QUERY_BUILDER_MODE',
  ADD_GROUPING_FOR_SUB_QUERY = '[RB] ADD_GROUPING_FOR_SUB_QUERY',
  SET_DIMENSIONS_BINNING_FOR_QUERY = '[RB] SET_DIMENSIONS_BINNING_FOR_QUERY',
  ADD_MEASURE_FOR_SUB_QUERY = '[RB] ADD_MEASURE_FOR_SUB_QUERY',
  REMOVE_MEASURE_FOR_SUB_QUERY = '[RB] REMOVE_MEASURE_FOR_SUB_QUERY',
  MOVE_MEASURE_FOR_SUB_QUERY = '[RB] MOVE_MEASURE_FOR_SUB_QUERY',
  SET_MEASURE_FOR_SUB_QUERY = '[RB] SET_MEASURE_FOR_SUB_QUERY',
  UPDATE_DIMENSION_FOR_SUB_QUERY = '[RB] UPDATE_DIMENSION_FOR_SUB_QUERY',
  ADD_DYNAMIC_FILTER_IN_SUB_QUERY = '[RB] ADD_DYNAMIC_FILTER_IN_SUB_QUERY',
  DELETE_DYNAMIC_FILTER_IN_SUB_QUERY = '[RB] DELETE_DYNAMIC_FILTER_IN_SUB_QUERY',
  SUB_QUERY_FILTER_VALUE_CHANGE = '[RB] SUB_QUERY_FILTER_VALUE_CHANGE',
  SUB_QUERY_SEARCH_FILTER_DATA_LOAD_REQUEST = '[RB] SUB_QUERY_SEARCH_FILTER_DATA_LOAD_REQUEST',
  SUB_QUERY_LOAD_MORE_FILTER_DATA_LOAD_REQUEST = '[RB] SUB_QUERY_LOAD_MORE_FILTER_DATA_LOAD_REQUEST',
  UPDATE_SUB_QUERY_FILTER_FORM = '[RB] UPDATE_SUB_QUERY_FILTER_FORM',
  SUB_QUERY_FILTER_LOAD_DATA_REQUEST = '[RB] SUB_QUERY_FILTER_LOAD_DATA_REQUEST',
  SUB_QUERY_SORT_COLUMN = '[RB] SUB_QUERY_SORT_COLUMN',
  DELETE_SUB_QUERY = '[RB] DELETE_SUB_QUERY',
  UPDATE_SUB_QUERY_DATA_SOURCE_JOINS_ARRAY = '[RB] UPDATE_SUB_QUERY_DATA_SOURCE_JOINS_ARRAY',
  UPDATE_SUB_QUERY_DATA_SOURCE_NAMESPACE = '[RB] UPDATE_SUB_QUERY_DATA_SOURCE_NAMESPACE',
  SET_QUERY_NAMESPACE = '[RB] SET_QUERY_NAMESPACE',
  UPDATE_SELECTED_QUERY = '[RB] UPDATE_SELECTED_QUERY',
  DELETE_SELECTED_QUERY = '[RB] DELETE_SELECTED_QUERY',
  RESET_SELECTED_QUERY = '[RB] RESET_SELECTED_QUERY',
  DELETE_UNSAVED_DATA_SOURCE = '[RB] DELETE_UNSAVED_DATA_SOURCE',
  SUBQUERY_ADD_OR_UPDATE_CUSTOM_EXPRESSION = '[RB] SUBQUERY_ADD_OR_UPDATE_CUSTOM_EXPRESSION',
  SUBQUERY_DELETE_CUSTOM_EXPRESSION = '[RB] SUBQUERY_DELETE_CUSTOM_EXPRESSION',
  SAVE_NESTED_JOINS_CONFIG = '[RB] SAVE_NESTED_JOINS_CONFIG',
  SAVE_SUBQUERY = '[RB] SAVE_SUBQUERY',
  UPDATE_JOIN_BLOCK_INDEX = '[RB] UPDATE_JOIN_BLOCK_INDEX',
  GET_SUB_QUERY_PREVIEW_DATA = '[RB] GET_SUB_QUERY_PREVIEW_DATA',
  SET_SUB_QUERY_PREVIEW_DATA_SUCCESS = '[RB] SET_SUB_QUERY_PREVIEW_DATA_SUCCESS',
  SET_SUB_QUERY_PREVIEW_DATA_FAILURE = '[RB] SET_SUB_QUERY_PREVIEW_DATA_FAILURE',
  SET_QUERY_EDIT_MODE = '[RB] SET_QUERY_EDIT_MODE',
  SET_DATA_SOURCE_EDIT_MODE = '[RB] SET_DATA_SOURCE_EDIT_MODE',
  SET_ACTIVE_SUBQUERY_PANEL = '[RB] SET_ACTIVE_SUBQUERY_PANEL',
  UPDATE_DATA_SOURCE_BREADCRUMB = '[RB] UPDATE_DATA_SOURCE_BREADCRUMB',
  UPDATE_INDEX_OF_JOIN_BLOCK_BEING_EDITED_INDEX = '[RB] UPDATE_INDEX_OF_JOIN_BLOCK_BEING_EDITED_INDEX',
  UPDATE_DRILLDOWN_MEASURES_SIDETRAY_STATE = '[RB] UPDATE_DRILLDOWN_MEASURES_SIDETRAY_STATE',
  UPDATE_DRILLDOWN_FIELDS_SIDETRAY_STATE = '[RB] UPDATE_DRILLDOWN_FIELDS_SIDETRAY_STATE',
  UPDATE_SUB_QUERY_PAGINATION_STATE = '[RB] UPDATE_SUB_QUERY_PAGINATION_STATE',
  RESET_QUERY_BUILDER_STATE = '[RB] RESET_QUERY_BUILDER_STATE',
  UPDATE_QUERY_BUILDER_STATE = '[RB] UPDATE_QUERY_BUILDER_STATE',
  UPDATE_PARENT_REPORT_DATA_SOURCE_SAVE_STATUS = '[RB] UPDATE_PARENT_REPORT_DATA_SOURCE_SAVE_STATUS',
  HANDLE_MAIN_REPORT_QUERY_FILTER_VALUE_CHANGE = '[RB] HANDLE_MAIN_REPORT_QUERY_FILTER_VALUE_CHANGE',
  HANDLE_MAIN_REPORT_FILTER_VALUE_CHANGE = '[RB] HANDLE_MAIN_REPORT_FILTER_VALUE_CHANGE',
  UPDATE_HIERARCHY_CONFIG = '[RB] UPDATE_HIERARCHY_CONFIG',
  UPDATE_USER_CONTEXT = '[RB] UPDATE_USER_CONTEXT',
  GET_META_SELECTOR_OPTIONS = '[RB] GET_META_SELECTOR_OPTIONS',
  SET_META_SELECTOR_OPTIONS = '[RB] SET_META_SELECTOR_OPTIONS',
  APPLY_ENTITY_GROUPING = '[RB] APPLY_ENTITY_GROUPING',
  SET_ENTITY_GROUPING = '[RB] SET_ENTITY_GROUPING',
  ADD_OR_UPDATE_CUSTOM_COLUMN_EXPRESSION = '[RB] ADD_OR_UPDATE_CUSTOM_COLUMN_EXPRESSION',
  UPDATE_REPORT_TYPE_FOR_BUILDER = '[RB] UPDATE_REPORT_TYPE_FOR_BUILDER',
  UPDATE_REPORT_TYPE_FOR_SUB_BUILDER = '[RB] UPDATE_REPORT_TYPE_FOR_SUB_BUILDER',
  GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_BUILDER = '[RB] GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_BUILDER',
  GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_SUBBUILDER = '[RB] GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_SUBBUILDER',
  RESET_REPORT_CONFIG_FOR_REPORT_TYPE_TOGGLE = '[RB] RESET_REPORT_CONFIG_FOR_REPORT_TYPE_TOGGLE ',
  UPDATE_LIST_REPORT_COLUMN_CONFIG_FOR_BUILDER = '[RB] UPDATE_LIST_REPORT_COLUMN_CONFIG_FOR_BUILDER',
  UPDATE_LIST_REPORT_COLUMN_CONFIG_FOR_SUBBUILDER = '[RB] UPDATE_LIST_REPORT_COLUMN_CONFIG_FOR_SUBBUILDER',
  REPORT_BUILDER_TYPE_CHANGE_REQUEST = '[RB] REPORT_BUILDER_TYPE_CHANGE_REQUEST',
  REPORT_BUILDER_TYPE_CHANGE_SUCCESS = '[RB] REPORT_BUILDER_TYPE_CHANGE_SUCCESS',
  REPORT_BUILDER_TYPE_CHANGE_FAILURE = '[RB] REPORT_BUILDER_TYPE_CHANGE_FAILURE',
  GET_DATA_SOURCES_FIELDS_REQUEST = '[RB] GET_DATA_SOURCES_FIELDS_REQUEST',
  SUB_QUERY_SWITCH_USER_FIELD_AND_FILTER ='[RB] SUB_QUERY_SWITCH_USER_FIELD_AND_FILTER',
  UPDATE_DIMENSION = '[RB] UPDATE_DIMENSION',
  UPDATE_CUSTOM_HIERARCHIES = '[RB] UPDATE_CUSTOM_HIERARCHIES',
  ADD_OR_UPDATE_FORMATTING_RULE = '[RB] ADD_OR_UPDATE_FORMATTING_RULE',
  DELETE_FORMATTING_RULE = '[RB] DELETE_FORMATTING_RULE',
  REORDER_FORMATTING_RULES = '[RB] REORDER_FORMATTING_RULES',
  CLEAR_FORMATTING_RULES = '[RB] CLEAR_FORMATTING_RULES',
  SET_FORMATTING_RULE_EDIT_MODE = '[RB] SET_FORMATTING_RULE_EDIT_MODE',
  CLEAR_CUSTOM_FILTER_DEFINITIONS = '[RB] CLEAR_CUSTOM_FILTER_DEFINITIONS',
  GET_ALL_REPORTS_FOR_LINKED_INSIGHTS = '[RB] GET_ALL_REPORTS_FOR_LINKED_INSIGHTS',
  GET_REPORT_CONFIG_FOR_LINKED_INSIGHTS = '[RB] GET_REPORT_CONFIG_FOR_LINKED_INSIGHTS',
  SAVE_LINKED_INSIGHT = '[RB] SAVE_LINKED_INSIGHT',
  DELETE_LINKED_INSIGHT = '[RB] DELETE_LINKED_INSIGHT',
}

export interface ISaveSuccessPayload {
  reportId: string;
  saveType: ReportBuilderStatus;
}

export interface IFilterDataLoadRequestPayload {
  filterId: string;
  variables: IVariables;
  filterValue?: any;
  isLazyLoad?: boolean;
  // When `filterId` belongs to a constituent of a custom filter, set this to the
  // wrapper's ID. Lets the loader fall back to searching per-query filter configs
  // for query-applied constituents that don't live in the main report's filterConfig
  // (e.g. the user hasn't visited the subquery, or the constituent's ID is stored
  // under a different shape in main filterConfig).
  associatedCustomFilterId?: string;
}

export interface ISearchFilterPayloadRequest {
  filterId: string;
  searchKey: string;
}
export interface ILoadMoreFilterPayloadRequest {
  filterId: string;
}

export interface IFilterReducerPayload {
  filterId: string;
  isLazyLoad: boolean;
}

export interface IFilterDataLoadPayload {
  filterId: string;
  filterResponse: IFilterResponse;
}

export interface IAddDynamicFilterPayload {
  index: number;
}

export interface IDeleteDynamicFilterPayload {
  filterId: string;
}

export interface IFilterValueChangePayload {
  filterId: string;
  filterValue: string;
  variables: IVariables;
}

export interface IFilterFormValues {
  name: string;
  filterId: string;
  // Scoped to standalone GroupMultiSelect (Sales Group) filters on the main report.
  hideFromReportPage?: boolean;
}

export interface IAddMeasurePayload {
  index: number;
  status: ColumnModes;
}

export interface IRemoveMeasurePayload {
  index: number;
}

export interface IMoveMeasurePayload {
  sourceIndex: number;
  destinationIndex: number;
}

export interface ISetMeasurePayload {
  index: number;
  newMeasure: IColumn;

}

export interface ISortColumnPayload {
  columnType: draggableItemType;
  columnName: string;
  order: SortingOrderTypes | null;
  multiSort: boolean;
  Id: string;
}
export interface ISortDrilldownColumnPayload {
  columnType: draggableItemType;
  columnName: string;
  order: SortingOrderTypes | null;
  multiSort: boolean;
  key?: string;
  Id: string;
}

export interface DrilldownConfigMapping {
  [key: string]: MeasureListWithJoinId[]; // Array of string representing measure names
}
export interface DrilldownColumnConfigResponse {
  [key: string]: {
    defaultColumns: IColumn[];
    sortState: IDrilldownSorting[];
  }
}

export interface IDrilldownSorting {
  Field: string;
  Direction: SortingOrderTypes;
  Id: string;
}
export interface DrilldownColumnConfig {
  [key: string]: DrillDownColumnConfigObj;
}
export interface DrillDownColumnConfigObj{
  defaultColumns:IColumn[],
  sortState: IDrilldownSorting[];
  additionalDrilldownColumns?: IColumn[];
  isAdditionalColumnFetched: boolean;
}

export interface DrilldownFieldUpdationsAction{
  column: IColumn;
  groupingId: string;
  type: DrilldownFieldUpdationType;
}

export enum DrilldownFieldUpdationType {
  ADD='add',
  REMOVE='remove'
}

export interface MeasureIdentifier {
  Entity: string;
  JoinId: string;
}

export interface MeasureListWithJoinId {
  Id: string;
  Name: string;
  JoinId: string;
  Entity: Omit<FieldEntitiesType, FieldEntitiesType.Custom>;
}

export interface MeasureIdentifiers {
  [key: string]: MeasureIdentifier;
}

export interface MeasureIdentifierPayload {
  DefaultDrillDownColumnsInput: MeasureIdentifiers;
}

export interface MeasureListPayload {
  [key: string]: string[];
}
export interface IRawTemplatePayload{
  namespace: string;
}

export interface ISaveReportPayload{
  data: IReportConfig,
  params?: ObjModel.Obj
}

export interface IDataSourceResponse{
  [key: string]: IDataSourceObjectInfo;
}
export interface IDataSourceObjectInfo{
  Entity: FieldEntitiesType,
    NamespaceId: string,
    DisplayName: string,
    DefaultField: IDataSourceFieldType,
    AdditionalFields: IDataSourceFieldType[],
    IsQuery: boolean,
}
export interface IDataSourceApiResponse{
    Entity: FieldEntitiesType,
    NamespaceId: string,
    DisplayName: string,
    DefaultField: IColumn,
    AdditionalFields: IColumn[];
}

export interface IDataSourceInfo{
  dataSources: IDataSourceResponse
}

export enum UpdateType {
  DataSource = 'DataSource',
  CustomColumns = 'CustomColumns'
}

export interface IResetReportConfigPayload{
  joinsData: JoinItem[],
  primaryNamespace: string,
  reportConfig?: IReportConfig
  originalReportId?:string
  pageContext?: PageContext
  variables?: IVariables
  selectedQueryId?:string
  force?: boolean
  updateType?: UpdateType
}

export interface IDataSourceFieldType{
  DataType: ColumnDataTypes,
  DisplayName: string,
  SchemaName: string,
}

export interface IDataSourceSavePayload{
  ReportConfig: IReportConfig,
  NewDataSources: string[],
}

export interface INestedQueryDataSourceSavePayload {
  BuilderRequest: {
    Namespaces: string[];
  };
}

export interface IQueryBuilder {
  SelectedQueryData: ISelectedQueryInfo,
  SelectedQuery : string,
  DynamicQueryList: Array<IQueryInfo>,
  StaticQueryList: Array<IQueryInfo>,
  PreviewData: IQueryPreviewData,
  BreadcrumbData: Array<IBreadcrumbItem>,
  DataSourceMapping: IDataSourceMapping, // curent data source mapping config
  DataSourceMappingPayload: IDataSourceMapping, // final data source config that will be send as a part of payload
}

export interface ISelectedQueryInfo {
  additionalData: IQueryBuilderAdditionalInfo,
  updatedInfo: IQueryBuilderUpdatedInfo,
  NameSpace: string,
  QueryJoinRelation: IQueryJoinRelation,
  Type: ReportType
}

export interface IQueryBuilderAdditionalInfo {
  AdditionalFilters: Array<ReportResponseModel.IFilterConfig>;
  AdditionalDimensions: Array<ReportResponseModel.IColumn>;
  AdditionalMeasures: Array<ReportResponseModel.IColumn>;
  AdditionalUserFieldFilters: Array<ReportResponseModel.IFilterConfig>;
  AdditionalUserFields: Array<ReportResponseModel.IColumn>;
  Fields:Array<IField>;
}

export interface IQueryBuilderUpdatedInfo {
  AppliedDimensions: IDimension[];
  AppliedMeasures: IColumn[];
  CalculatedColumns: Set<string>;
  ExpressionStore: ObjModel.ObjGeneric<ICustomExpression>;
  filterBuilder: IFilterBuilder;
  sortState: ISorting[];
  Paging: IPaging
}

export interface IQueryInfo {
  Id: string,
  Name: string,
  LinkedQueryIds: Array<string>,
  ParentQueryId?: string,
  Type: ReportType,
  QueryPayload: IBuilder, // payload of apply-report-joins
  QueryJoinRelation: IQueryJoinRelation,
  IsDraft: boolean,
  QueryConfig?: IRawTemplate, // response of save-nested-joins api
  QueryDataInfo: ISelectedQueryInfo // latest changes done to query
}

export interface IDataSourceItems {
  Joins: Array<JoinItem>,
  PrimaryNamespace: string,
  JoinBlockIndexBeingEdited: number,
  JoinBlockConditionIndexBeingEdited?: number,
  isEditMode: boolean,
  isDataSourceSaved: boolean,
}

export interface IDataSourceMapping {
  [key: string]: IDataSourceItems;
}

export interface IQueryJoinRelation {
  JoinBlockindex: number;
  JoinBlockFieldSelection: DataSourceSelectionType;
  ParentQueryId: string;
  conditionIndex?: number;
}

export enum ReportType {
  Summary = 'Summary',
  List = 'List'
}

export interface IGroupedDataSources {
  [entityType: string]: IDataSourceObjectInfo[];
}

export interface ISetDimensionPayload {
  index: number;
  displayName: string;
  description?: string;
  Formatter?: any;
  Mode?: ColumnModes;
}

export interface IConditionalFormatting {
  Enabled: boolean;
  Rules: IRule[];
}

export interface IRule {
  Id: string,
  Name: string,
  DataType: ColumnDataTypes,
  ColumnsIds: string[],
  ApplyTo: {
    Cell: boolean,
    Row: boolean
  },
  Format: IFormattingStyleConfig,
}

export interface IFormattingStyleConfig {
  Type: FORMAT_TYPES,
  Config: ISingleColorConfig | IColorRangeConfig,
}
export interface ISingleColorConfig {
  Condition: ICondition,
  BackgroundColor: string,
  TextColor: string,
  IsCaseSensitive?: boolean
}
export interface ICondition {
  Operator: string, // will change to enum of operatorTypes
  Values: any,
}

export interface IColorRangeConfig {
  ColorShades: string[],
  Min: {
    value: any,
  },
  Mid?: {
    value: any,
  },
  Max: {
    value: any,
  },
}

export interface ILinkedConfig{
  MeasureIdList:string[],
  LinkedReportId:string,
  LinkingConfig: ILinkedFieldsConfig;
}

export interface ISaveLinkedInsightPayload {
  config: ILinkedConfig;
  editIndex: number;
}

export interface ILinkedFieldsConfig{
  Dimensions: {
    [key: string]: string;
  };
  Filters: {
    [key: string]: string;
  };
}
